import { UTurnLeftOutlined } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import {
  ORDER_STATUS,
  PRODUCT_CATEGORY,
} from "../../../constants/applicationConstants";
import {
  setSingleAlertObj,
  SingleAlertInfo,
} from "../../../redux/slice/commonSlice";
import {
  getOrdersByPatientId,
  OrderInfo,
} from "../../../redux/slice/order/OrderSlice";
import {
  getPatientListByPracticeId,
  resetPatientListByPracticeId,
  usePatientReducer,
} from "../../../redux/slice/patient/patientSlice";
import { PracticeInfo } from "../../../redux/slice/practiceSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useUserReducer } from "../../../redux/slice/userSlice";
import { fullName, sortNames, getCategoryDisplayLabel } from "../../../utils/Utility";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { useModalStyles } from "../../common/useModalStyles";

interface ReturnProductModalProps {
  open: boolean;
  onClose: () => void;
}

interface ReturnFormData {
  practice: string | number;
  patientId: string | number;
  orderId: string | number;
}

type DropdownOption = { value: string | number; label: string };

const RETURN_ACCENT = "#ef4444";

const ReturnProductModal: React.FC<ReturnProductModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    isDark,
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    dropdownWrapSx,
    destructiveBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const { practiceByUserRoleUserSlice } = useUserReducer();
  const { patientList } = usePatientReducer();
  const { allCodeTablesValuesMap } = useCodetablesSlice();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [returnableOrders, setReturnableOrders] = useState<OrderInfo[]>([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ReturnFormData>({
    defaultValues: { practice: "", patientId: "", orderId: "" },
    mode: "onSubmit",
  });

  const selectedPractice = watch("practice");
  const selectedPatientId = watch("patientId");

  useEffect(() => {
    if (open) {
      dispatch(resetPatientListByPracticeId());
      setReturnableOrders([]);
      if (practiceByUserRoleUserSlice?.length === 1) {
        const singleId = practiceByUserRoleUserSlice[0].practiceId;
        setValue("practice", singleId);
        loadPatientsForPractice(singleId.toString());
      }
    }
  }, [open]);

  useEffect(() => {
    if (selectedPractice) {
      dispatch(resetPatientListByPracticeId());
      setValue("patientId", "");
      setValue("orderId", "");
      setReturnableOrders([]);
      loadPatientsForPractice(selectedPractice.toString());
    }
  }, [selectedPractice]);

  useEffect(() => {
    if (selectedPatientId) {
      setValue("orderId", "");
      setReturnableOrders([]);
      loadReturnableOrdersForPatient(Number(selectedPatientId));
    }
  }, [selectedPatientId]);

  const loadPatientsForPractice = async (practiceId: string) => {
    setLoadingPatients(true);
    await dispatch(getPatientListByPracticeId({ practiceId }));
    setLoadingPatients(false);
  };

  const loadReturnableOrdersForPatient = async (patientId: number) => {
    setLoadingOrders(true);
    try {
      const response = await dispatch(getOrdersByPatientId(patientId));

      if (
        response.payload?.code === "V3OK" &&
        response.payload?.message === "SUCCESS" &&
        (!response.payload?.errors || response.payload.errors.length === 0)
      ) {
        const allOrders: OrderInfo[] = response.payload.data ?? [];

        const returnable = allOrders.filter(
          (o) =>
            o.orderStatus === ORDER_STATUS.DELIVERED &&
            o.productCategory !== PRODUCT_CATEGORY.CONSERVATIVE &&
            o.manufacturerName !== "Migrate Manufacturer",
        );

        if (returnable.length === 0) {
          showAlert(
            `No returnable orders found for this patient. Only delivered, ${getCategoryDisplayLabel(allCodeTablesValuesMap, PRODUCT_CATEGORY.ADVANCED)} orders are eligible for return.`,
            "error",
          );
          return;
        }

        setReturnableOrders(returnable);
      } else {
        showAlert(
          "An error occurred while fetching orders for this patient.",
          "error",
        );
      }
    } catch {
      showAlert(
        "An error occurred while fetching orders. Please try again.",
        "error",
      );
    } finally {
      setLoadingOrders(false);
    }
  };

  const showAlert = (
    message: string,
    alertType: "success" | "error" | "info",
  ) => {
    const alertObj: SingleAlertInfo = { message, alertType };
    dispatch(setSingleAlertObj(alertObj));
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setReturnableOrders([]);
      dispatch(resetPatientListByPracticeId());
      reset();
    }, 300);
  };

  const onFormSubmit = async (data: ReturnFormData) => {
    if (!data.patientId || !data.orderId) return;
    setIsSubmitting(true);

    try {
      const selectedOrder = returnableOrders.find(
        (o) => o.orderId === Number(data.orderId),
      );

      if (!selectedOrder) {
        showAlert("Selected order not found. Please try again.", "error");
        return;
      }

      const matchedPatient = patientList?.find(
        (p: any) => p.patientId === Number(data.patientId),
      );
      const patientName = matchedPatient
        ? fullName(
            "",
            matchedPatient.firstName,
            matchedPatient.middleName,
            matchedPatient.lastName,
          )
        : "";

      const enrichedOrder: OrderInfo = {
        ...selectedOrder,
        patientName,
        manufacturerName: selectedOrder.manufacturerName ?? "",
      };

      navigate("/returns", {
        state: { orderDetails: enrichedOrder },
      });

      handleClose();
    } catch {
      showAlert("An error occurred. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const practiceOptions: DropdownOption[] = useMemo(() => {
    if (!practiceByUserRoleUserSlice?.length) return [];
    const sorted = sortNames(practiceByUserRoleUserSlice, "practiceName");
    return sorted.map((p: PracticeInfo) => ({
      value: p.practiceId,
      label: p.practiceName,
    }));
  }, [practiceByUserRoleUserSlice]);

  const patientOptions: DropdownOption[] = useMemo(() => {
    if (!patientList?.length) return [];
    return patientList.map((p: any) => ({
      value: p.patientId,
      label:
        fullName("", p.firstName, p.middleName, p.lastName) +
        " — DOB: " +
        p.birthDate,
    }));
  }, [patientList]);

  const orderOptions: DropdownOption[] = useMemo(() => {
    return returnableOrders.map((o) => ({
      value: o.orderId,
      label: `${o.orderNumber}${o.productName ? " — " + o.productName : ""}`,
    }));
  }, [returnableOrders]);

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: RETURN_ACCENT }}>
          *
        </Box>
      )}
    </Typography>
  );

  const ErrorMsg = ({ msg }: { msg?: string }) =>
    msg ? (
      <Typography sx={{ color: tokens.error, fontSize: "0.75rem", mt: 0.5 }}>
        {msg}
      </Typography>
    ) : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(RETURN_ACCENT)}>
          <UTurnLeftOutlined
            sx={{ fontSize: "1.2rem", color: RETURN_ACCENT }}
          />
        </Box>
        <Box>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: tokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Return Product
          </Typography>
          <Typography
            sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}
          >
            Select practice, patient and order
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Box sx={bodySx}>
          <Box>
            <FieldLabel label="Practice" required />
            <Box sx={dropdownWrapSx(!!errors.practice)}>
              <CustomDropdownField
                name="practice"
                control={control}
                options={practiceOptions}
                rules={{ required: "Please select a practice to continue." }}
                size="small"
                placeholderText="Select a practice..."
                sortOptions={false}
                disabled={practiceOptions.length === 1}
              />
            </Box>
          </Box>
          <Box>
            <FieldLabel label="Patient" required />
            <Box sx={dropdownWrapSx(!!errors.patientId)}>
              <CustomDropdownField
                name="patientId"
                control={control}
                options={patientOptions}
                rules={{ required: "Please select a patient to continue." }}
                size="small"
                placeholderText={
                  !selectedPractice
                    ? "Select a practice first..."
                    : loadingPatients
                      ? "Loading patients..."
                      : patientOptions.length === 0
                        ? "No active patients found..."
                        : "Select a patient..."
                }
                sortOptions={false}
                disabled={!selectedPractice || loadingPatients}
              />
            </Box>
          </Box>
          <Box>
            <FieldLabel label="Delivered Order" required />
            <Box sx={dropdownWrapSx(!!errors.orderId)}>
              <CustomDropdownField
                name="orderId"
                control={control}
                options={orderOptions}
                rules={{ required: "Please select an order to continue." }}
                size="small"
                placeholderText={
                  !selectedPatientId
                    ? "Select a patient first..."
                    : loadingOrders
                      ? "Loading orders..."
                      : orderOptions.length === 0
                        ? "No eligible orders found..."
                        : "Select an order..."
                }
                sortOptions={false}
                disabled={
                  !selectedPatientId ||
                  loadingOrders ||
                  orderOptions.length === 0
                }
              />
            </Box>
            {selectedPatientId &&
              !loadingOrders &&
              orderOptions.length === 0 && (
                <Typography
                  sx={{
                    fontSize: "0.76rem",
                    color: tokens.textSecondary,
                    mt: 0.5,
                  }}
                >
                  Only delivered, {getCategoryDisplayLabel(allCodeTablesValuesMap, PRODUCT_CATEGORY.ADVANCED)} orders are eligible for return.
                </Typography>
              )}
          </Box>
        </Box>

        <Box sx={footerSx}>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={isSubmitting || loadingOrders}
              startIcon={
                <UTurnLeftOutlined sx={{ fontSize: "1rem !important" }} />
              }
              sx={destructiveBtnSx}
            >
              {isSubmitting ? "Processing..." : "Return"}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              onClick={handleClose}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default ReturnProductModal;
